<template>
    <div class="date-input-container">
        <label :for="`date-picker-${label}`" class="label-date" :class="dateModel || dateFocus ? 'active-label' : ''">
            <span>{{ label }}</span>
        </label>
        <img class="icon" :src="calendarIcon" :alt="$t('alt.selectionner.date')" @click="focusInput" />

        <input
            :id="`date-picker-${label}`"
            type="text"
            v-model="displayDate"
            @focus="dateFocus = true"
            @blur="handleBlur"
            @input="handleManualInput"
            class="text-input"
            :class="{ 'date-input-novisible': !dateFocus && !dateModel }"
            aria-describedby="date-format-hint"
        />

        <input
            ref="datePicker"
            type="date"
            :min="dateMin"
            :max="dateMax"
            v-model="dateModel"
            class="date-input"
            @change="handleDateSelection"
            tabindex="-1"
            aria-hidden="true"
        />
        <span id="date-format-hint" class="visually-hidden">{{$t('date.format')}}</span>
    </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
    data() {
        return {
            dateFocus: false,
            displayDate: '',
            internalDateValue: '',
        };
    },
    props: {
        label: { type: String, required: true },
        dateMin: { type: String, required: false },
        dateMax: { type: String, required: false },
        dateValue: { type: String, required: true },
    },
    computed: {
        dateModel: {
            get() {
                return this.internalDateValue || this.$props.dateValue;
            },
            set(value) {
                this.internalDateValue = value;
                this.$emit('setDate', value);
            },
        },
        calendarIcon() {
            return require('@/assets/img/calendar.svg');
        }
    },
    methods: {
        focusInput() {
            const input = document.getElementById(`date-picker-${this.label}`);
            if (input) input.focus();
        },
        formatDateForDisplay(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            if (!this.isValidDate(date)) return '';

            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();

            return `${day}/${month}/${year}`;
        },

        parseDisplayDate(displayStr) {
            const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
            const match = displayStr.match(regex);

            if (!match) return '';

            const [, day, month, year] = match;
            const date = new Date(year, month - 1, day);
            if (!this.isValidDate(date)) return '';

            if (date.getDate() !== parseInt(day) ||
                (date.getMonth() + 1) !== parseInt(month) ||
                date.getFullYear() !== parseInt(year)) {
                return '';
            }

            return `${year}-${month}-${day}`;
        },

        handleManualInput(event) {
            let input = event.target.value.replace(/[^\d/]/g, '');

            if (input.length === 2 || input.length === 5) {
                if (!input.endsWith('/')) {
                    input += '/';
                }
            }

            if (input.length > 10) {
                input = input.slice(0, 10);
            }

            this.displayDate = input;

            if (input.length === 10) {
                const isoDate = this.parseDisplayDate(input);
                if (isoDate) {
                    this.dateModel = isoDate;
                    if (this.$refs.datePicker) {
                        this.$refs.datePicker.value = isoDate;
                    }
                }
            } else if (input.length === 0) {
                this.dateModel = '';
                if (this.$refs.datePicker) {
                    this.$refs.datePicker.value = '';
                }
            }
        },

        handleDateSelection(event) {
            const newDate = event.target.value;
            this.dateModel = newDate;
            this.displayDate = this.formatDateForDisplay(newDate);
        },

        handleBlur() {
            this.dateFocus = false;

            if (this.displayDate) {
                const isoDate = this.parseDisplayDate(this.displayDate);
                if (!isoDate) {
                    this.displayDate = this.formatDateForDisplay(this.dateModel);
                } else {
                    this.dateModel = isoDate;
                }
            }
        },

        isValidDate(date) {
            return date instanceof Date && !isNaN(date);
        },

        isWithinDateLimits(dateStr) {
            if (!dateStr) return true;
            const date = new Date(dateStr);
            const min = this.dateMin ? new Date(this.dateMin) : null;
            const max = this.dateMax ? new Date(this.dateMax) : null;

            if (min && date < min) return false;
            if (max && date > max) return false;
            return true;
        }
    },

    watch: {
        dateValue: {
            immediate: true,
            handler(newValue) {
                this.internalDateValue = newValue;
                this.displayDate = this.formatDateForDisplay(newValue);
            }
        }
    },

    mounted() {
        if (this.dateValue) {
            this.displayDate = this.formatDateForDisplay(this.dateValue);
        }
    }
});
</script>

<style scoped>
.text-input {
    border: none;
    outline: none;
    flex: 1;
    color: var(--black);
    font-family: monospace;
    background: transparent;
    z-index: 1;
}

.date-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
}

.date-input-container {
    position: relative;
    height: 50px;
    box-sizing: border-box;
    display: flex;
    border: 1px solid var(--medium-dark-grey);
    border-radius: 4px;
    padding: 11px 12px;
    align-items: center;
}
.icon {
    margin: 0;
    height: 24px;
    width: 24px;
    justify-self: end;
}
.label-date span {
    font-family: 'Roboto';
}
.label-date {
    left: 36px;
    display: flex;
    height: 20px;
    align-items: center;
    position: absolute;
    pointer-events: none;
    transition: 0.2s ease all;
    padding: 2px;
    background-color: var(--white);
    color: var(--medium-dark-grey);
    top: calc(50% - 11px);
    font-size: var(--font-size-low-medium);
}
.date-input-novisible{
    color: white !important;
}
.active-label {
    top: -7px;
    height: 12px;
    left: 12px;
    font-size: var(--font-size-very-small);
    width: auto;
}
.date-input {
    border: none;
    outline: none;
    flex: 1;
    color: var(--black);
    cursor: pointer;
}
.date-input {
    border: none;
    outline: none;
    flex: 1;
    color: var(--black);
}
.date-input::-webkit-calendar-picker-indicator {
    position: absolute;
    right: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    opacity: 0;
    cursor: pointer;
}
.visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
</style>
